import { type CSSProperties, useMemo } from 'react';

import classNames from 'classnames';

import { PriceInterval, type Tier } from '@/types';

import GiftCheckoutForm from './components/GiftCheckoutForm';
import InternalCheckout from './components/InternalCheckout';
import { Tab } from './components/Tab';
import { Tabs } from './components/Tabs';
import TierCard from './components/TierCard';

import { isLightColor } from '../utils/colorContrast';
import { currencyFormatter } from '../utils/currencyFormatter';
import { computeFont } from '../utils/fonts';
import { useIsMobile } from '../utils/useIsMobile';

import * as styles from './Pricing.css';
import type { PricingElementProps } from './Pricing.types';

export function PricingElement(props: PricingElementProps) {
  const {
    element,
    children,
    attributes,
    className,
    selectedTier,
    onTierClick,
    selectedPrice,
    onPriceClick,
    isStripeLoading,
    email,
    onEmailChange,
    showTaxIdField,
    taxId,
    onTaxIdChange,
    donationAmountCents,
    onDonationAmountCentsChange,
    isSubmitting,
    StripeCardElement,
    StripePaymentElement,
    PaymentRequestButtonElement,
    isInternalCheckout,
    hasTermsOfService,
    hasCustomTermsOfService,
    currentFormMode,
    onCurrentFormModeChange,
    isLive,
    translations,
    isMobileView,
  } = props;

  const isMobileLiveDetection = useIsMobile();

  const {
    mobile_hide,
    desktop_hide,
    customAttributes,
    padding,
    gap,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    cardShadow,
    cardBackgroundColor,
    cardPadding,
    nameColor,
    descriptionColor,
    nameSelectedColor,
    descriptionSelectedColor,
    featureSelectedColor,
    featureSelectedIconColor,
    priceColor,
    priceDenominatorColor,
    priceFontSize,
    priceDenominatorFontSize,
    offerPriceColor,
    offerPriceFontSize,
    offerDetailsColor,
    offerDetailsIconColor,
    featureColor,
    featureIconColor,
    buttonBorderRadius,
    buttonBorderWidth,
    buttonBorderColor,
    buttonBorderStyle,
    buttonColor,
    buttonTextColor,
    inputBorderRadius,
    inputBorderWidth,
    inputBorderColor,
    inputBorderStyle,
    tabBackgroundColor,
    tabSelectedBackgroundColor,
    tabTextColor,
    tabSelectedTextColor,
    tabBorderRadius,
    tabBorderWidth,
    tabBorderColor,
    tabBorderStyle,
    tabsContainerBorderRadius,
    tabsContainerBorderWidth,
    tabsContainerBorderColor,
    tabsContainerBorderStyle,
    recommendedBackgroundColor,
    recommendedTextColor,
    recommendedBorderRadius,
    showRecommendedTierFirstOnMobile,
    recommendedTierId,
    recommendedPriceId,
    inputBackgroundColor,
    inputTextColor,
    inputPlaceholderColor,
    fontFamily,
    data,
    giftTiersData,
    isGiftingEnabled,
    isGiftingOnly,
    giftFormHeadingTextColor,
    giftFormHeadingFontSize,
    giftFormLabelTextColor,
    giftFormLabelFontSize,
    giftTabHeadingText,
    giftTabHeadingFontSize,
    giftTabHeadingTextColor,
    additionalFieldsData,
  } = element.attrs || {};

  // The value of memoized renderable tiers is derived keeping in
  // mind the two problems below

  // Problem
  // - A Tier can be disabled so it's not visible to the visitors
  // Solution
  // - Filter out the disabled tiers by asserting `tiers.enabled` attribute

  // Problem
  // - A Tier has many prices/intervals (monthly/yearly/donation/one-time)
  // - By default, if there is more than one price, they are shown as tabs
  //   in the same tier card
  // - Users can decide to split prices/intervals in a tier
  //   to display as separate cards
  // - When tier.should_split_prices is true, we display prices in that tier
  //   as separate cards
  // Solution
  // - Loop through filtered/enabled tiers and split prices if `tier.should_split_prices`
  //   is true
  // - Example; if a Tier has two prices, monthly and yearly, we will then
  //   have two tiers, with one price each. The tiers will be presented to the
  //   visitor as separate cards, one with monthly pricing and the other one
  //   with yearly pricing
  const renderableTiers: Tier[] = useMemo(() => {
    const alteredTiers: Tier[] = [];

    ((currentFormMode === 'gift' ? giftTiersData : data) || [])
      .filter((tier) => !!tier.enabled)
      .forEach((tier) => {
        if (tier.should_split_prices && tier.prices.length > 1) {
          tier.prices.forEach((price) => {
            alteredTiers.push({
              ...tier,
              prices: [price],
            });
          });
        } else {
          alteredTiers.push(tier);
        }
      });

    // Sort tiers to show recommended tier first on mobile if enabled
    if (showRecommendedTierFirstOnMobile && (recommendedTierId || recommendedPriceId)) {
      // Use isMobileView prop if provided (editor context), otherwise use reactive hook (live site)
      const isMobile = isMobileView !== undefined ? isMobileView : isMobileLiveDetection;

      if (isMobile) {
        const recommendedIndex = alteredTiers.findIndex((tier) => {
          if (recommendedTierId && tier.id === recommendedTierId) {
            // If we have both tier and price ID, ensure the price exists in this tier
            if (recommendedPriceId) {
              return tier.prices.some((price) => price.id === recommendedPriceId);
            }
            return true;
          }
          if (recommendedPriceId && tier.prices.some((price) => price.id === recommendedPriceId)) {
            return true;
          }
          return false;
        });

        if (recommendedIndex > 0) {
          const [recommendedTier] = alteredTiers.splice(recommendedIndex, 1);
          alteredTiers.unshift(recommendedTier);
        }
      }
    }

    return alteredTiers;
  }, [
    data,
    giftTiersData,
    currentFormMode,
    showRecommendedTierFirstOnMobile,
    recommendedTierId,
    recommendedPriceId,
    isMobileView,
    isMobileLiveDetection,
  ]);

  const evaluatedInternalCheckoutCta = useMemo(() => {
    if (currentFormMode === 'gift' && element.attrs?.giftFormCheckoutCta) {
      return element.attrs.giftFormCheckoutCta;
    }

    if (currentFormMode === 'individual' && element.attrs?.internalCheckoutCta) {
      return element.attrs.internalCheckoutCta;
    }

    const isDonation = selectedPrice?.interval === PriceInterval.DONATION;
    const isPaidTrial = selectedPrice?.premium_offer?.offer_type === 'paid_trial';
    const amountCents =
      (isDonation
        ? donationAmountCents
        : isPaidTrial && selectedPrice?.premium_offer?.paid_trial_price_cents != null
          ? selectedPrice.premium_offer.paid_trial_price_cents
          : selectedPrice?.amount_cents) || 0;

    return selectedPrice
      ? `Pay ${currencyFormatter({ value: amountCents / 100, currency: selectedPrice.currency })}`
      : 'Submit';
  }, [element.attrs?.internalCheckoutCta, selectedPrice, donationAmountCents]);

  // Shows tabs to a visitor to chose an option between
  // buying the subscription as an individual or
  // as a gift for someone else
  // Note: If gifting is only enabled, don't show the tabs
  const showFormModeTabs = useMemo(() => {
    if (isGiftingEnabled && isGiftingOnly) {
      return false;
    }

    return (
      (!isLive && isGiftingEnabled) ||
      (isGiftingEnabled &&
        giftTiersData &&
        Array.isArray(giftTiersData) &&
        giftTiersData.length > 0 &&
        currentFormMode &&
        onCurrentFormModeChange)
    );
  }, [isLive, isGiftingEnabled, giftTiersData, currentFormMode, onCurrentFormModeChange, isGiftingOnly]);

  const additionalFieldsStyle = useMemo(() => {
    return {
      borderColor: inputBorderColor,
      borderWidth: inputBorderWidth,
      borderRadius: inputBorderRadius,
      borderStyle: inputBorderStyle,
      color: inputTextColor,
      '--placeholder-color': inputPlaceholderColor,
    } as CSSProperties;
  }, [inputBorderColor, inputBorderWidth, inputBorderRadius, inputBorderStyle, inputTextColor, inputPlaceholderColor]);

  return (
    <div
      className="relative w-fit"
      style={
        {
          // Card
          '--card-padding': cardPadding,
          '--card-background': cardBackgroundColor,
          '--card-shadow': cardShadow,
          '--card-height': '100%',

          '--name-color': nameColor,
          '--description-color': descriptionColor,
          '--feature-color': featureColor,
          '--feature-icon-color': featureIconColor,
          '--name-selected-color': nameSelectedColor,
          '--description-selected-color': descriptionSelectedColor,
          '--feature-selected-color': featureSelectedColor,
          '--feature-selected-icon-color': featureSelectedIconColor,
          '--price-color': priceColor,
          '--price-denominator-color': priceDenominatorColor,
          '--price-font-size': priceFontSize,
          '--price-denominator-font-size': priceDenominatorFontSize,
          '--offer-price-color': offerPriceColor,
          '--offer-price-font-size': offerPriceFontSize,
          '--offer-details-color': offerDetailsColor,
          '--offer-details-icon-color': offerDetailsIconColor,

          // Button
          '--button-color': buttonColor,
          '--button-text-color': buttonTextColor,
          '--button-border-radius': buttonBorderRadius,
          '--button-border-color': buttonBorderColor,
          '--button-border-width': buttonBorderWidth,
          '--button-border-style': buttonBorderStyle,

          // Input
          '--input-border-radius': inputBorderRadius,
          '--input-border-width': inputBorderWidth,
          '--input-border-color': inputBorderColor,
          '--input-border-style': inputBorderStyle,
          '--input-font-size': '14px',
          '--input-background-color': inputBackgroundColor,
          '--input-text-color': inputTextColor,
          // Pricing has no separate label color prop, so fall back to inputTextColor
          '--input-label-color': inputTextColor,
          '--input-placeholder-color': inputPlaceholderColor,
          '--input-calendar-icon-filter': isLightColor(inputTextColor) ? 'invert(1)' : 'none',

          // Additional Fields
          '--input-checkbox-radius': inputBorderRadius,
          '--input-radio-checkbox-background-color': inputTextColor,
          '--input-radio-checkbox-accent-color': inputBorderColor,

          // Tab
          '--tab-background-color': tabBackgroundColor,
          '--tab-selected-background-color': tabSelectedBackgroundColor,
          '--tab-text-color': tabTextColor,
          '--tab-selected-text-color': tabSelectedTextColor,
          '--tab-border-radius': tabBorderRadius,
          '--tab-border-color': tabBorderColor,
          '--tab-border-width': tabBorderWidth,
          '--tab-border-style': tabBorderStyle,
          '--tabs-container-border-radius': tabsContainerBorderRadius,
          '--tabs-container-border-color': tabsContainerBorderColor,
          '--tabs-container-border-width': tabsContainerBorderWidth,
          '--tabs-container-border-style': tabsContainerBorderStyle,

          '--recommended-background-color': recommendedBackgroundColor,
          '--recommended-text-color': recommendedTextColor,
          '--recommended-border-radius': recommendedBorderRadius,

          // Gift
          '--gift-tab-heading-text-color': giftTabHeadingTextColor,
          '--gift-tab-heading-font-size': giftTabHeadingFontSize,
          '--gift-form-heading-text-color': giftFormHeadingTextColor,
          '--gift-form-heading-font-size': giftFormHeadingFontSize,
          '--gift-form-label-text-color': giftFormLabelTextColor,
          '--gift-form-label-font-size': giftFormLabelFontSize,
        } as CSSProperties
      }
      {...attributes}
    >
      <div
        {...customAttributes}
        className={classNames(
          'flex flex-col mx-auto w-fit items-center',
          className,
          {
            'mobile:hidden': mobile_hide,
            'desktop:hidden': desktop_hide,
          },
          styles.pricingContainer,
          customAttributes?.className,
        )}
        style={{
          gap: gap,
          padding,
          borderStyle,
          borderWidth,
          borderRadius,
          borderColor,
          fontFamily: computeFont(fontFamily) || 'inherit',
        }}
      >
        {showFormModeTabs && (
          <Tabs>
            <Tab
              key="individual-tab"
              isSelected={currentFormMode === 'individual'}
              label={translations?.['upgrade.tabs.individual.title'] || 'Individual'}
              onClick={() => onCurrentFormModeChange?.('individual')}
            />
            <Tab
              key="gift-tab"
              isSelected={currentFormMode === 'gift'}
              label={translations?.['upgrade.tabs.gift.title'] || 'Gift'}
              onClick={() => onCurrentFormModeChange?.('gift')}
            />
          </Tabs>
        )}
        {currentFormMode === 'gift' && giftTabHeadingText ? (
          <h2 className={classNames(styles.giftTabHeading)}>{giftTabHeadingText}</h2>
        ) : null}
        <div
          className={classNames('flex flex-col sm:flex-row sm:flex-wrap sm:justify-center h-full')}
          style={
            {
              gap: gap,
              width: '100%',
            } as CSSProperties
          }
        >
          {renderableTiers.map((tier, index) => (
            <TierCard
              key={`${index}-${tier.id}-${currentFormMode}`}
              tier={tier}
              attributes={element.attrs}
              selectedTier={selectedTier}
              selectedPrice={selectedPrice}
              setSelectedTier={onTierClick}
              setSelectedPrice={onPriceClick}
              isInternalCheckout={isInternalCheckout}
              donationAmountCents={donationAmountCents}
              setDonationAmountCents={onDonationAmountCentsChange}
              isCtaDisabled={isStripeLoading || isSubmitting}
              translations={translations}
              currentFormMode={currentFormMode}
            />
          ))}
        </div>
        {currentFormMode === 'gift' && (
          <GiftCheckoutForm
            translations={translations}
            email={email}
            onEmailChange={onEmailChange}
            showTaxIdField={showTaxIdField}
            taxId={taxId}
            onTaxIdChange={onTaxIdChange}
            ctaText={evaluatedInternalCheckoutCta}
            isCtaDisabled={isStripeLoading || isSubmitting || !selectedTier}
            StripeCardElement={StripeCardElement}
            StripePaymentElement={StripePaymentElement}
            selectedTier={selectedTier}
            selectedPrice={selectedPrice}
            PaymentRequestButtonElement={PaymentRequestButtonElement}
            hasTermsOfService={hasTermsOfService}
            hasCustomTermsOfService={hasCustomTermsOfService}
            inputTextColor={inputTextColor}
            inputPlaceholderColor={inputPlaceholderColor}
            inputBackgroundColor={inputBackgroundColor}
            isInternalCheckout={isInternalCheckout}
          />
        )}
        {isInternalCheckout && currentFormMode === 'individual' && (
          <InternalCheckout
            email={email}
            onEmailChange={onEmailChange}
            showTaxIdField={showTaxIdField}
            taxId={taxId}
            onTaxIdChange={onTaxIdChange}
            ctaText={evaluatedInternalCheckoutCta}
            isCtaDisabled={isStripeLoading || isSubmitting || !selectedTier}
            StripeCardElement={StripeCardElement}
            StripePaymentElement={StripePaymentElement}
            selectedTier={selectedTier}
            selectedPrice={selectedPrice}
            PaymentRequestButtonElement={PaymentRequestButtonElement}
            hasTermsOfService={hasTermsOfService}
            hasCustomTermsOfService={hasCustomTermsOfService}
            inputTextColor={inputTextColor}
            inputPlaceholderColor={inputPlaceholderColor}
            inputBackgroundColor={inputBackgroundColor}
            additionalFieldsData={additionalFieldsData}
            additionalFieldsStyle={additionalFieldsStyle}
          />
        )}
      </div>
      {children}
    </div>
  );
}
